/**
 * TeacherProfileTab — admin edit of a teacher's profile (Teacher doc).
 *
 * User-doc fields (name, ring, roles, etc.) are NOT edited here; the
 * caller already has a user-edit flow in UserDetail. This tab focuses
 * on Teacher-doc fields and shares its form with the teacher app via
 * `@shared-ui/teacher-profile-form`.
 *
 * API: GET /teacher/profile?targetUserId=<id>, PUT /teacher/profile
 * with `targetUserId` in the body. Admin gating + tenant scope live
 * server-side in Backend/functions/src/teacher/teacherProfile.ts.
 */

import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { TeacherProfileForm } from '@shared-ui/teacher-profile-form';
import type { TeacherProfileFormValue } from '@shared-ui/teacher-profile-form';
import { userApi, type AdminTeacherDoc } from '../../../api/userApi';

interface TeacherProfileTabProps {
  userId: string;
}

const EMPTY_VALUE: TeacherProfileFormValue = {
  subjects: [],
  grades: [],
};

function toFormValue(doc: AdminTeacherDoc): TeacherProfileFormValue {
  return {
    displayName: doc.displayName,
    subjects: doc.subjects ?? [],
    grades: doc.grades ?? [],
    bio: doc.bio,
    pastResults: doc.pastResults,
    discoverable: doc.discoverable,
    instructions: doc.instructions,
  };
}

export function TeacherProfileTab({ userId }: TeacherProfileTabProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-teacher-profile', userId],
    queryFn: () => userApi.getTeacherProfile(userId),
    enabled: !!userId,
  });

  const [value, setValue] = useState<TeacherProfileFormValue>(EMPTY_VALUE);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (data) {
      setValue(toFormValue(data));
      setIsDirty(false);
    }
  }, [data]);

  const updateMutation = useMutation({
    mutationFn: (next: TeacherProfileFormValue) => userApi.updateTeacherProfile(userId, next),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-teacher-profile', userId] });
      setIsDirty(false);
    },
  });

  const handleChange = (next: TeacherProfileFormValue) => {
    setValue(next);
    setIsDirty(true);
  };

  const handleSave = () => updateMutation.mutate(value);

  if (isLoading) return <div className="loading">Loading teacher profile...</div>;
  if (error) {
    const msg = error instanceof Error ? error.message : 'Failed to load';
    if (msg.toLowerCase().includes('not found')) {
      return <p className="no-features-message">This user does not have a teacher profile.</p>;
    }
    return <p className="status-error">{msg}</p>;
  }

  return (
    <div className="features-section" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <TeacherProfileForm
        value={value}
        onChange={handleChange}
        disabled={updateMutation.isPending}
      />

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <button
          className="btn btn-secondary"
          onClick={handleSave}
          disabled={!isDirty || updateMutation.isPending}
          style={{ padding: '6px 14px', borderRadius: 6, fontSize: 13, fontWeight: 600 }}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </button>
        {updateMutation.isError && (
          <span className="status-error">
            Failed: {(updateMutation.error as Error).message}
          </span>
        )}
        {updateMutation.isSuccess && !isDirty && <span className="status-success">Saved</span>}
      </div>
    </div>
  );
}
